import { useState, useRef, useEffect } from "react";

interface UseNotifySocialMediaPreviewParams {
  headline: string;
  tagline: string;
  content: string;
  citySeal: string;
  onComplete?: () => void;
}
export default function useNotifySocialMediaPreview({
  headline,
  tagline,
  content,
  citySeal,
  onComplete,
}: UseNotifySocialMediaPreviewParams) {
  const [backgroundImage] = useState(new Image());
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);
  const [logoImage] = useState(new Image());
  const [logoImageLoaded, setLogoImageLoaded] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctx = canvasRef.current?.getContext("2d");
  const firstImage = getFirstImageUrlFromNotificationContent(content);

  useEffect(() => {
    backgroundImage.onload = () => {
      setBackgroundImageLoaded(true);
    };
    backgroundImage.src = firstImage;

    logoImage.onload = () => {
      setLogoImageLoaded(true);
    };
    logoImage.src = citySeal ?? "";
  }, [content, citySeal]);

  const canDrawLogo = !citySeal || logoImageLoaded;
  const canDrawBackground = !firstImage || backgroundImageLoaded;

  useEffect(() => {
    if (ctx && canDrawLogo && canDrawBackground) {
      const canvas = ctx.canvas;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (backgroundImage) {
        /**
         * TLDR: The code draws the background image on the canvas, cropping it to fit the canvas size while maintaining the
         * aspect ratio.
         *
         * The code replicates the implementation of some CSS properties:
         *
         *   object-fit: cover
         *   object-position: 50% 50%
         *
         * @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
         * @see https://stackoverflow.com/questions/10285134/whats-the-math-behind-csss-background-sizecover
         *
         * Long term, this code is probably not maintainable. I know, I know, I should have used HTML and CSS for this from
         * the get go. But I had originally written up the solution using canvas, and it was faster for me to keep it this
         * way.
         */
        const canvasBackgroundHeight = canvas.height / 2;
        const canvasAspect = canvas.width / canvasBackgroundHeight;
        const imgAspect = backgroundImage.width / backgroundImage.height;

        let sx, sy, sWidth, sHeight;

        if (imgAspect > canvasAspect) {
          // Image is wider than canvas --> crop width
          sHeight = backgroundImage.height;
          sWidth = canvasAspect * sHeight;
          sx = (backgroundImage.width - sWidth) / 2;
          sy = 0;
        } else {
          // Image is taller than canvas --> crop height
          sWidth = backgroundImage.width;
          sHeight = sWidth / canvasAspect;
          sx = 0;
          sy = (backgroundImage.height - sHeight) / 2;
        }

        ctx.drawImage(backgroundImage, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvasBackgroundHeight);
      }

      const canvasCenterX = canvas.width / 2;
      const canvasCenterY = canvas.height / 2;
      const newLogoWidth = canvas.width / 8;
      const newLogoHeight = canvas.height / 8;
      const newLogoX = canvasCenterX - newLogoWidth / 2;
      const newLogoY = canvasCenterY - newLogoHeight / 2;

      if (logoImage) {
        ctx.drawImage(
          logoImage,
          0,
          0,
          logoImage.width,
          logoImage.height,
          newLogoX,
          newLogoY,
          newLogoWidth,
          newLogoHeight,
        );
      }

      const headerLineSize = canvas.height / 16;

      ctx.fillStyle = "black";
      ctx.font = `${headerLineSize}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const lines = getLines(ctx, headline, canvas.width - canvas.width / 4);

      const headlineStartY = canvasCenterY + canvas.height / 8;
      lines.forEach((line, index) => {
        ctx.fillText(line, canvasCenterX, headlineStartY + index * headerLineSize);
      });

      const taglineLineSize = headerLineSize - headerLineSize / 3;
      ctx.fillStyle = "gray";
      ctx.font = `${taglineLineSize}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const taglineLines = getLines(ctx, tagline, canvas.width - canvas.width / 8);
      taglineLines.forEach((line, index) => {
        ctx.fillText(line, canvasCenterX, headlineStartY + lines.length * headerLineSize + index * taglineLineSize);
      });

      if (onComplete) {
        /**
         * When puppeteer goes to take a screenshot, sometimes, it takes it before the browser has had a chance
         * to render the canvas... at least, I think that is what is happening -- Peter
         */
        const duration = 2000;
        setTimeout(() => {
          onComplete();
        }, duration);
      }
    }
  }, [logoImageLoaded, backgroundImageLoaded, tagline, headline, content]);

  return { canvasRef };
}

/**
 * Extracts the first image URL from the content body of a notification.
 *
 * @param content the content body from the notification
 * @returns the first image URL from the content
 */
const getFirstImageUrlFromNotificationContent = (content: string): string => {
  const imageUrlRegex = /!\[.*?\]\((https?:\/\/[^\s)]+)\)/g;
  const firstImageUrl = imageUrlRegex.exec(content);
  if (firstImageUrl && firstImageUrl.length > 1) {
    return firstImageUrl[1];
  }
  return "";
};

/**
 * Some code I found online for rendering lines with a max width.
 *
 * @see https://stackoverflow.com/questions/2936112/text-wrap-in-a-canvas-element
 *
 * @param ctx the 2d rendering context
 * @param text the text to render
 * @param maxWidth the maximum width of the text
 *
 * @returns the lines to render
 */
const getLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const words = text.split(" ");
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + " " + word).width;
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};
